import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'jumpgame', label: 'Jump Game' },
  { key: 'jumpgame2', label: 'Jump Game II' },
  { key: 'gasstation', label: 'Gas Station' },
  { key: 'partitionlabels', label: 'Partition Labels' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

const BoxRow = ({ label, values, getBoxState }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--muted)', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', minHeight: 46 }}>
      {values.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--muted)', padding: '12px 0' }}>empty</span>}
      {values.map((v, i) => {
        const st = getBoxState ? getBoxState(i) : {};
        return (
          <div key={i} style={{
            width: 44, height: 44, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800, fontFamily: "'Inter',sans-serif",
            background: st.bg || 'var(--bg3)', color: st.color || 'var(--text)',
            border: `1.5px solid ${st.border || 'var(--border)'}`, transition: 'all 0.2s ease',
          }}>{v}</div>
        );
      })}
    </div>
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 220 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={{
      padding: '8px 14px', borderRadius: 9, cursor: step === 0 ? 'default' : 'pointer',
      border: `1.5px solid ${step === 0 ? 'var(--border)' : V1}`, background: 'transparent',
      color: step === 0 ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
      fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: step === 0 ? 0.6 : 1,
    }}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

/* ─── Jump Game ───────────────────────────────────────────────────── */
const JumpGameMode = () => {
  const [input, setInput] = useState('2, 3, 1, 1, 4');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const nums = input.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
    if (nums.length === 0 || nums.length > 10) return;
    const built = [];
    let farthest = 0;
    for (let i = 0; i < nums.length; i++) {
      if (i > farthest) {
        built.push({ i, farthest, stuck: true, note: `i=${i}: this index is beyond farthest reach (${farthest}) — unreachable. Return false immediately.` });
        break;
      }
      farthest = Math.max(farthest, i + nums[i]);
      built.push({ i, farthest, note: `i=${i}: ${i <= farthest ? `still reachable (i ≤ farthest).` : ''} farthest = max(farthest, ${i} + ${nums[i]}) = ${farthest}.` });
    }
    const last = built[built.length - 1];
    if (!last.stuck) {
      built.push({ i: nums.length - 1, farthest: last.farthest, done: true, note: `Loop finished without ever getting stuck — the last index (${nums.length - 1}) was always reachable. Answer: true.` });
    } else {
      built.push({ done: true, failed: true, note: `Answer: false.` });
    }
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const nums = input.split(',').map(s => s.trim()).filter(Boolean);
  const getBoxState = i => {
    if (!cur) return {};
    if (cur.i === i) return { bg: cur.stuck ? `linear-gradient(135deg,${RED},#c0293a)` : `linear-gradient(135deg,${V1},${V2})`, border: cur.stuck ? RED : V1, color: '#fff' };
    if (cur.farthest !== undefined && i <= cur.farthest && i < nums.length) return { border: GREEN };
    return {};
  };

  const code = {
    lines: [
      'boolean canJump(int[] nums) {',
      '    int farthest = 0;',
      '    for (int i = 0; i < nums.length; i++) {',
      '        if (i > farthest) return false;',
      '        farthest = Math.max(farthest, i + nums[i]);',
      '    }',
      '    return true;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? (cur.failed ? -1 : 6) : cur.stuck ? 3 : 4,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="nums (max jump length from each index)" values={nums} getBoxState={getBoxState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="2, 3, 1, 1, 4 (max 10)" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter jump lengths, then Run — watch the single "farthest reach" variable decide everything.` : cur.note}
          {cur && cur.farthest !== undefined && <> <Chip color={TEAL}>farthest = {cur.farthest}</Chip></>}
        </p>
      </div>
      <CodePanel title="Java — Jump Game (Greedy)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Jump Game II ────────────────────────────────────────────────── */
const JumpGameIIMode = () => {
  const [input, setInput] = useState('2, 3, 1, 1, 4');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const nums = input.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
    if (nums.length === 0 || nums.length > 10) return;
    const built = [];
    let jumps = 0, currentEnd = 0, farthest = 0;
    for (let i = 0; i < nums.length - 1; i++) {
      farthest = Math.max(farthest, i + nums[i]);
      if (i === currentEnd) {
        jumps++;
        currentEnd = farthest;
        built.push({ i, farthest, currentEnd, jumps, note: `i=${i}: farthest=max(farthest, ${i}+${nums[i]})=${farthest}. i reached currentEnd — this jump's range is exhausted: jumps=${jumps}, currentEnd becomes ${currentEnd}.` });
      } else {
        built.push({ i, farthest, currentEnd, jumps, note: `i=${i}: farthest=max(farthest, ${i}+${nums[i]})=${farthest}. Still within the current jump's range (i < currentEnd=${currentEnd}).` });
      }
    }
    built.push({ done: true, jumps, note: `Loop ends (no jump needed from the last index itself). Final answer: ${jumps} jumps.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const nums = input.split(',').map(s => s.trim()).filter(Boolean);
  const getBoxState = i => {
    if (!cur || cur.done) return {};
    if (cur.i === i) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    if (cur.currentEnd !== undefined && i <= cur.currentEnd) return { border: GREEN };
    return {};
  };

  const code = {
    lines: [
      'int jump(int[] nums) {',
      '    int jumps = 0, currentEnd = 0, farthest = 0;',
      '    for (int i = 0; i < nums.length - 1; i++) {',
      '        farthest = Math.max(farthest, i + nums[i]);',
      '        if (i == currentEnd) {',
      '            jumps++;',
      '            currentEnd = farthest;',
      '        }',
      '    }',
      '    return jumps;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 9 : 3,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="nums (max jump length from each index)" values={nums} getBoxState={getBoxState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="2, 3, 1, 1, 4 (max 10)" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `This variant assumes the end is always reachable — it counts the MINIMUM jumps needed.` : cur.note}
          {cur && cur.jumps !== undefined && <> <Chip color={GREEN}>jumps = {cur.jumps}</Chip></>}
        </p>
      </div>
      <CodePanel title="Java — Jump Game II (Greedy, Level Expansion)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Gas Station ─────────────────────────────────────────────────── */
const GasStationMode = () => {
  const [gasInput, setGasInput] = useState('1, 2, 3, 4, 5');
  const [costInput, setCostInput] = useState('3, 4, 5, 1, 2');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const gas = gasInput.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
    const cost = costInput.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
    if (gas.length === 0 || gas.length !== cost.length || gas.length > 8) return;
    const built = [];
    let total = 0, tank = 0, start = 0;
    for (let i = 0; i < gas.length; i++) {
      const diff = gas[i] - cost[i];
      total += diff;
      tank += diff;
      if (tank < 0) {
        built.push({ i, tank, total, start, resetting: true, note: `i=${i}: diff=${gas[i]}-${cost[i]}=${diff}. tank=${tank} (negative!) — station ${start} through ${i} can't be a valid start. Reset: start=${i + 1}, tank=0.` });
        start = i + 1;
        tank = 0;
      } else {
        built.push({ i, tank, total, start, note: `i=${i}: diff=${gas[i]}-${cost[i]}=${diff}. tank=${tank} (still non-negative) — start=${start} survives.` });
      }
    }
    const feasible = total >= 0;
    built.push({ done: true, feasible, start: feasible ? start : -1, total, note: feasible ? `Loop ends. total=${total} ≥ 0 — a valid circuit exists, starting at index ${start}.` : `Loop ends. total=${total} < 0 — not enough gas overall; no valid starting index exists. Answer: -1.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const gas = gasInput.split(',').map(s => s.trim()).filter(Boolean);
  const cost = costInput.split(',').map(s => s.trim()).filter(Boolean);
  const getGasState = i => (cur && !cur.done && cur.i === i) ? { bg: cur.resetting ? `linear-gradient(135deg,${RED},#c0293a)` : `linear-gradient(135deg,${V1},${V2})`, border: cur.resetting ? RED : V1, color: '#fff' } : {};

  const code = {
    lines: [
      'int canCompleteCircuit(int[] gas, int[] cost) {',
      '    int total = 0, tank = 0, start = 0;',
      '    for (int i = 0; i < gas.length; i++) {',
      '        int diff = gas[i] - cost[i];',
      '        total += diff;',
      '        tank += diff;',
      '        if (tank < 0) {',
      '            start = i + 1;',
      '            tank = 0;',
      '        }',
      '    }',
      '    return total >= 0 ? start : -1;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 11 : cur.resetting ? 7 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="gas" values={gas} getBoxState={getGasState} />
        <BoxRow label="cost" values={cost} getBoxState={getGasState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={gasInput} onChange={e => { setGasInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1, 2, 3, 4, 5" />
          <input value={costInput} onChange={e => { setCostInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="3, 4, 5, 1, 2" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter equal-length gas/cost arrays (max 8), then Run.` : cur.note}
          {cur && cur.tank !== undefined && <> <Chip color={TEAL}>tank = {cur.tank}</Chip><Chip color={V2}>total = {cur.total}</Chip></>}
        </p>
      </div>
      <CodePanel title="Java — Gas Station (Greedy)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Partition Labels ────────────────────────────────────────────── */
const PartitionLabelsMode = () => {
  const [input, setInput] = useState('caedbdedda');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const s = input.slice(0, 12);
    if (s.length === 0) return;
    const last = {};
    for (let i = 0; i < s.length; i++) last[s[i]] = i;
    const built = [];
    let start = 0, end = 0;
    const partitions = [];
    for (let i = 0; i < s.length; i++) {
      end = Math.max(end, last[s[i]]);
      if (i === end) {
        partitions.push(end - start + 1);
        built.push({ i, end, cut: true, partitions: [...partitions], note: `i=${i} ('${s[i]}'): end=max(end, last['${s[i]}']=${last[s[i]]})=${end}. i==end → cut! Partition length ${end - start + 1} (indices ${start}..${end}).` });
        start = i + 1;
      } else {
        built.push({ i, end, partitions: [...partitions], note: `i=${i} ('${s[i]}'): end=max(end, last['${s[i]}']=${last[s[i]]})=${end}. Still growing this partition.` });
      }
    }
    built.push({ done: true, partitions, note: `Final partition lengths: [${partitions.join(', ')}].` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const s = input.slice(0, 12).split('');
  const getCharState = i => {
    if (!cur || cur.done) return {};
    if (cur.i === i) return { bg: cur.cut ? `linear-gradient(135deg,${GREEN},#27AE60)` : `linear-gradient(135deg,${V1},${V2})`, border: cur.cut ? GREEN : V1, color: '#fff' };
    if (cur.end !== undefined && i <= cur.end) return { border: TEAL };
    return {};
  };

  const code = {
    lines: [
      'List<Integer> partitionLabels(String s) {',
      '    int[] last = new int[26];',
      "    for (int i = 0; i < s.length(); i++) last[s.charAt(i) - 'a'] = i;",
      '    List<Integer> result = new ArrayList<>();',
      '    int start = 0, end = 0;',
      '    for (int i = 0; i < s.length(); i++) {',
      "        end = Math.max(end, last[s.charAt(i) - 'a']);",
      '        if (i == end) {',
      '            result.add(end - start + 1);',
      '            start = i + 1;',
      '        }',
      '    }',
      '    return result;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 12 : cur.cut ? 8 : 6,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="s" values={s} getBoxState={getCharState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="caedbdedda (max 12 chars)" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a lowercase string (max 12 chars), then Run — watch "end" track how far the current partition must extend.` : cur.note}
          {cur && cur.partitions && cur.partitions.length > 0 && <> <Chip color={GREEN}>partitions: [{cur.partitions.join(', ')}]</Chip></>}
        </p>
      </div>
      <CodePanel title="Java — Partition Labels (Greedy)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const farthest = 1 + Math.floor(Math.random() * 10);
  const index = 1 + Math.floor(Math.random() * 12);
  const correct = index <= farthest ? 'reachable' : 'stuck';
  return { farthest, index, correct };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  const options = [
    { key: 'reachable', label: `Reachable — continue the loop (index ${scenario.index} ≤ farthest ${scenario.farthest}?)` },
    { key: 'stuck', label: `Stuck — return false immediately (index ${scenario.index} > farthest ${scenario.farthest}?)` },
  ];

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '4px 0 14px', lineHeight: 1.6 }}>
        Jump-Game-style greedy: the farthest index reached so far is {scenario.farthest}. The loop is currently at index {scenario.index}. What should happen?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {options.map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o.key} onClick={() => pick(o.key)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const GreedyPlayground = () => {
  const [mode, setMode] = useState('jumpgame');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'jumpgame' && <JumpGameMode />}
      {mode === 'jumpgame2' && <JumpGameIIMode />}
      {mode === 'gasstation' && <GasStationMode />}
      {mode === 'partitionlabels' && <PartitionLabelsMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default GreedyPlayground;
